import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { formatMm } from '@bw/core';
import { Plus, Trash2, PencilLine, FileInput } from 'lucide-react';
import { useEditor } from '../state/store.js';

/**
 * Every wall in the project, in one place.
 *
 * Wall management used to be spread over four: a dropdown in the command bar,
 * an "Add wall" button at the bottom of the inspector, a second wall list
 * inside the plan panel, and the [ and ] keys. Nothing named the set of walls
 * as a thing you own, so the answer to "how do I add a second wall and lay it
 * out?" was not anywhere on screen.
 *
 * This is that list, and it is the only one. It sits at the top of the right
 * panel in every stage, so the wall you are drawing and the wall you are
 * positioning are visibly the same wall.
 */
export function WallsPanel({
  run,
  onSelectWall,
  onDeleteWall,
}: {
  readonly run: (id: string) => void;
  readonly onSelectWall: (id: string) => void;
  readonly onDeleteWall: (id: string) => void;
}): JSX.Element {
  const project = useEditor((s) => s.project);
  const touch = useEditor((s) => s.touch);
  useEditor((s) => s.docRevision);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (renaming !== null) field.current?.select();
  }, [renaming]);

  const commit = (): void => {
    if (renaming === null) return;
    const name = draft.trim();
    if (name !== '' && project.renameWall(renaming, name)) touch();
    setRenaming(null);
  };

  const active = project.activeWallId;
  const only = project.walls.length === 1;

  return (
    <section>
      <h2>Walls</h2>
      <div className="walllist">
        {project.walls.map((wall) => {
          const placement = wall.placement;
          const at = `X ${formatMm(placement.origin.x)} · Z ${formatMm(
            placement.origin.z,
          )} · ${formatMm(placement.rotationDeg)}°`;
          return renaming === wall.id ? (
            <input
              key={wall.id}
              ref={field}
              className="wallrename"
              data-text-input
              aria-label={`Rename ${wall.name}`}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onBlur={commit}
              onKeyDown={(event) => {
                if (event.key === 'Enter') commit();
                if (event.key === 'Escape') setRenaming(null);
              }}
            />
          ) : (
            <button
              key={wall.id}
              className="wallbtn"
              aria-pressed={wall.id === active}
              onClick={() => onSelectWall(wall.id)}
              onDoubleClick={() => {
                setDraft(wall.name);
                setRenaming(wall.id);
              }}
            >
              <span className="name">{wall.name}</span>
              <span className="count">{wall.model.size}</span>
              <span className="dim">{at}</span>
            </button>
          );
        })}
      </div>
      <div className="actions">
        <button className="btn" onClick={() => run('wall.add')} title="Add a wall">
          <Plus size={14} /> Add wall
        </button>
        <button
          className="btn"
          onClick={() => run('wall.import')}
          title="Add a wall from a .brickwall.json file made by the desktop tool"
        >
          <FileInput size={14} /> Import wall
        </button>
        <button
          className="btn"
          onClick={() => {
            setDraft(project.activeWall.name);
            setRenaming(active);
          }}
          title="Rename this wall (or double-click it)"
        >
          <PencilLine size={14} /> Rename
        </button>
        <button
          className="btn danger icononly"
          disabled={only}
          onClick={() => onDeleteWall(active)}
          aria-label="Delete this wall"
          title={only ? 'A project keeps at least one wall' : 'Delete this wall'}
        >
          <Trash2 size={14} />
        </button>
      </div>
      <p className="note">
        A new wall starts where this one ends, turned a quarter turn, so walls added in order walk
        round the outline — imported ones too, which is how a house that arrives as one file per
        elevation goes back together. Press [ and ] to step through them.
      </p>
    </section>
  );
}
